/* eslint-disable  react/destructuring-assignment */
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import React, { Component } from 'react';
import { interpolateString, transforms, masks, pageContext, get } from 'ctm-util-functions';
import { formPayloadSelector } from 'ctm-selectors';

import { Snapshot } from '../snapshot';

/**
 * Maps to state and prepares the correct data to pass to our visual snapshot component
 * @returns {Node} - the Snapshot component.
 */
class _SnapshotContainer extends Component {

    render = () => {
        return (
            <Snapshot
                contentProps={this.props.contentProps}
                visualProps={{ ...this.props.visualProps }}
            />
        );
    };

}

_SnapshotContainer.propTypes = {
    contentProps: PropTypes.object,
    visualProps: PropTypes.object
};

/**
 * Use util functions to connect to state, and format the correct props to send to the visual component
 * @param {Object} state - application store
 * @param {Object} args - The destructured args
 * @param {Object} args.dataProps - props explicitly used for state data fetching / manipulation
 * @param {Object} args.contentProps - content to pass to visual component
 * @param {Object} args.visualProps - content to pass to visual component
 * @returns {{contentProps: {snapshotItems: *}, visualProps: *}} - the correctly formatted props for the visual component
 */
const mapStateToProps = (state, { dataProps, contentProps, visualProps }) => {
    const snapshotItems = getSnapshotDataFromState(dataProps.snapshotItems, formPayloadSelector(state, 'vertical.quote.form'));
    return {
        contentProps: {
            ...contentProps,
            snapshotItems
        },
        visualProps
    };
};

/**
 * Apply a transform described in a snapshot item value descriptor to a value retrieved from state
 * @param {String} valueDesc - The snapshot item value descriptior, starting with 'transform:'
 * @param {Object} state - The app state
 * @returns {*} - The transformed value
 */
const applyTransform = (valueDesc, state) => {
    if (valueDesc.match(/[:]/g).length !== 2) {
        throw Error(`Snapshot attempted to use an invalid transform definition: ${valueDesc}. 
                                All transform definitions should be in the form: transform:transformName:stateIdentifier`);
    }
    const [prefix, transformName, identifier] = valueDesc.split(':');
    const transform = transforms[transformName];
    if (transform === undefined) {
        throw Error(`Snapshot attempted to use a transform that doesn't exist: ${transformName}`);
    }
    // the transform may return undefined - this ok, we just don't want to show the value in this case, so set it to null.
    return transform({ value: state.getIn(['vertical', 'quote', 'form', ...identifier.split('.')]) } || {}) || null;
};

/**
 * Apply a selector described in a snapshot item value descriptor to a value retrieved from state
 * @param {String} valueDesc - The snapshot item value descriptior, starting with 'transform:'
 * @param {Object} state - The app state
 * @returns {*} - The transformed value
 */
const applySelector = (valueDesc, state) => {
    const selectors = pageContext.getProps().verticalSelectors;
    const [prefix, selectorName] = valueDesc.split(':');
    const selector = selectors[selectorName];
    if (selector === undefined) {
        throw Error(`Snapshot attempted to use a selector that doesn't exist: ${selectorName}`);
    }
    return selector({ state });
};

/**
 * Apply a value mapping to a resolved snapshot item value, according to the value described for the item
 * @param {Object} item - The snapshot item configuration
 * @param {*} value - The resolved value for the snapshot - ie with any transform / selector applied
 * @returns {*} - The mapped value
 */
const applyValueMapping = (item, value) => {
    const valueMap = pageContext.getProps().valueMap['vertical.quote'];
    // Apply any value mapping defined for this snapshot item
    // @todo: look into using get() for this, value could contain spaces, dots etc
    let mappedValue = item.valueMap ? item.valueMap[value] || value : value;

    // Apply the valueMask is specified, throw an error for an invalid valueMask specification
    const valueMask = item.valueMask;
    if (valueMask && !masks[valueMask]) {
        throw new Error(`SnapshotContainer attempted to use a valueMask: ${valueMask} that doesn't exist`);
    }

    mappedValue = valueMask ? masks[valueMask](mappedValue, ',') : mappedValue;
    mappedValue = get(valueMap, mappedValue, mappedValue);

    const valueTemplate = item.valueTemplate;
    if (valueTemplate && valueTemplate[mappedValue]) {
        // if there is an explicit valueTemplate for mappedValue
        mappedValue = interpolateString(valueTemplate[mappedValue])({ mappedValue });
    } else if (valueTemplate && valueTemplate['*'] && value) {
        // if we want to apply the value mapping regardless of the value of mappedValue
        mappedValue = interpolateString(valueTemplate['*'])({ mappedValue });
    }
    return mappedValue;
};

/**
 * A util function used to fetch state values for particular keys, formats an array of { title, value } objects
 * and returns without any 'null' elements within the array
 * @param {Object} snapshotData - array of elements to map state data
 * @param {Object} state - application store state
 * @returns {Array} an array of snapshot items
 */
const getSnapshotDataFromState = (snapshotData, state) => {
    return snapshotData.map((item) => {
        // reduce each item to string with possible multiple values separated by a space
        const value = item.value.reduce((acc, val) => {
            if (typeof val !== 'string') {
                throw Error(`All snapshot item value definitions must be strings, received: ${val}`);
            }
            let data;
            if (val.startsWith('transform:')) {
                data = applyTransform(val, state);
            } else if (val.startsWith('selector:')) {
                data = applySelector(val, state);
            } else {
                data = state.getIn(['vertical', 'quote', 'form', ...val.split('.')]);
            }
            if (data === undefined) {
                // We only need to check that the data doesn't actually exist on state, if data itself is falsy (especially null)
                // that's ok as it'll get caught and filtered out below. We also don't want to throw error as this could run in
                // SSR where snapshot data is undefined while we are trying to load joinery to results page
                data = null;
            }
            return data !== null ? acc.concat([data]) : acc;
        }, []).join(' ');

        if (!value || (item.visibilityValue && value !== item.visibilityValue)) {
            return null;
        }

        return { link: item.link, title: item.title, value: applyValueMapping(item, value) };
    }).filter((n) => n !== null);
};

export const SnapshotContainer = connect(mapStateToProps)(_SnapshotContainer);

export const testExports = {
    _SnapshotContainer,
    getSnapshotDataFromState
};
